import { useCallback, useEffect, useRef, useState } from 'react';
import { acceptHeldFix, isCurrentFix, isRoughFix, isVisitFix, type GeoFix } from '@/capture/shotGeo';
import type { GeoWatchState } from '@/capture/useGeoWatch';
import { useLatestFix } from '@/capture/useLatestFix';
import { useT } from '@/i18n/runtime';
import { detectPlatform, detectWebview, iosLocationEntry, locationOffStepsFor, queryPermissionState, type Platform } from '@/media/permissions';
import type { GeoPermissionReport, GeoPhase } from '@/media/useGeoEvidence';
import { Button } from './components/Button';
import { RecoveryCard } from './help/RecoveryCard';

/**
 * US-03482 P1-3 (decided 2026-11-02 by the author): no shot starts until this phone has given a position of this visit
 * (`shotGeo.isCurrentFix`). Blocking after a DENIAL (E1, 2026-11-03) is pending owner confirmation.
 *
 * Why a gate: on Android the browser permission can be "Allow" while the phone's own Location (GPS) is OFF — Chrome
 * then answers POSITION_UNAVAILABLE / TIMEOUT, no web page can switch the location on, and the visit-level row used to
 * fall through silently, so every shot bound no position.
 *
 * Since the iPhone black-video RCA (G2, 2026-11-03) the gate makes NO position request of its own: it reads the
 * capture's ONE shared watch (capture/useGeoWatch.ts, owned by CaptureFlow) and the visit-level report built from it
 * (useGeoEvidence). Its "re-try" restarts that one watch — on Try again, on a return to the page, on a permission
 * change, and every few seconds while Location is off or slow (alternating high / low accuracy). Chrome on iOS
 * re-prompts on every new acquisition; one watch means one prompt.
 *
 *   • Never stuck: the watch has its own watchdog (a browser that never answers reads as "unavailable"); a restart is a
 *     new probe, and a late answer from the watch it replaced is ignored.
 *   • A re-try never replaces the screen: the steps, the single `role="alert"` and the button stay mounted; progress is
 *     an inline line; the button is disabled while the watch has not answered yet. A screen reader hears the progress
 *     from a polite live region ONCE per reason — again only when the reason changes or the customer presses Try again,
 *     never every 8 s.
 *   • Error code 1, iPhone FIRST: Safari's "Don't Allow" sticks until the page is reloaded (and Safari may not report a
 *     permission state at all), so on iOS it is "reload this page, then tap Allow when asked" with a Reload button —
 *     unless the site permission reads `granted`, which on iOS means Location Services is off (the Settings steps).
 *     Elsewhere: `prompt` = dismissed ("tap Allow when asked"), `granted` = blocked below the browser — §16i's "Location
 *     off" (the device's Location and the browser app's own permission), `denied` / unknown = the browser's recovery
 *     card. Blocked states re-try only on a permission change, a return to the page, or Try again — never on a timer.
 *   • The browser's question may still be on screen (`asking`, §16i): "tap Allow when asked", no timer; the answer (a
 *     permission change) re-tries at once.
 *   • TIMEOUT has its own words.
 *   • While the page is hidden (the customer is in Settings) nothing is restarted; back on the page the watch is
 *     restarted at once, even if the one left behind has not answered (it may have been opened with Location off).
 *   • E4 (2026-11-03): a rough fix (worse than ±500 m) never blocks for long — the gate waits for the watch to bring a
 *     better one for up to ROUGH_WAIT_MS, then accepts it; the accuracy still travels with every chunk, so the report
 *     can label it. A fix that is old by its own clock (shotGeo.acceptHeldFix) gets the same wait: a phone that answers
 *     is never stranded.
 *
 * No permission screen of our own: the browser asks only while its permission is `prompt`. The gate posts no geo rows:
 * only the visit-level hook writes them.
 */
export type GateStatus = 'open' | 'waiting' | 'off' | 'denied' | 'unsupported';

/** Whether CaptureFlow mounts the gate: open with a current fix; a browser with no Geolocation API is let through. */
export function locationGateStatus(fix: GeoFix | null, phase: GeoPhase, permission: GeoPermissionReport): GateStatus {
  if (fix) return 'open';
  if (permission === 'UNSUPPORTED') return 'unsupported';
  if (permission === 'DENIED' || phase === 'denied') return 'denied';
  if (permission === 'TIMEOUT' || permission === 'ERROR' || phase === 'degraded') return 'off';
  return 'waiting';
}

/** What the screen says — read from the visit-level report, refined for code 1 by the browser's permission state. */
export type GateReason = 'waiting' | 'refining' | 'asking' | 'off' | 'timeout' | 'dismissed' | 'ios-reload' | 'denied' | 'webview';

/** The reason from the visit-level report (refined for code 1 by the browser's permission state). */
export function initialReason(phase: GeoPhase, permission: GeoPermissionReport, platform: Platform = 'other'): GateReason {
  if (phase === 'asking') return 'asking'; // the browser's question may still be up: no failure is declared
  if (permission === 'DENIED' || phase === 'denied') return platform === 'ios' ? 'ios-reload' : 'denied';
  if (permission === 'TIMEOUT') return 'timeout';
  if (permission === 'ERROR' || phase === 'degraded') return 'off';
  return 'waiting';
}

/** Error code 1 read with the platform FIRST (iPhone), then the browser's permission state. */
export function reasonForDenied(state: PermissionState | null, platform: Platform): GateReason {
  if (platform === 'ios') return state === 'granted' ? 'off' : 'ios-reload';
  if (state === 'denied' || state === null) return 'denied';
  if (state === 'prompt') return 'dismissed';
  return 'off'; // granted, yet refused: below the browser (§16i "Location off")
}

/** Re-start the watch on a timer only where the answer can change on its own (the phone finding a position). */
export function retriesOnTimer(reason: GateReason): boolean {
  return reason === 'off' || reason === 'timeout';
}

export const AUTO_RETRY_MS = 8_000;
/** E4: how long a held-back fix waits while the watch brings a better one; then it is accepted. */
export const ROUGH_WAIT_MS = 15_000;

const LEAD: Record<Exclude<GateReason, 'waiting' | 'refining'>, string> = {
  asking: 'sv.geo.gate.asking.lead',
  off: 'sv.geo.gate.off.lead',
  timeout: 'sv.geo.gate.timeout.lead',
  dismissed: 'sv.geo.gate.dismissed.lead',
  'ios-reload': 'sv.geo.gate.ios.lead',
  denied: 'sv.geo.gate.denied.lead',
  webview: 'sv.geo.gate.webview.lead',
};
const IOS_RELOAD_STEPS = ['sv.geo.gate.ios.1', 'sv.geo.gate.ios.2', 'sv.geo.gate.ios.3'];

export function LocationGate({ phase, permission, watch, tenantName, ua, reload }: {
  phase: GeoPhase;
  permission: GeoPermissionReport;
  /** The capture's ONE shared position watch (G2). */
  watch: GeoWatchState;
  tenantName: string;
  ua?: string;
  /** Test seam; the page reload by default. */
  reload?: () => void;
}): JSX.Element {
  const { t } = useT();
  const agent = ua ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const platform = detectPlatform(agent);
  // An in-app browser (Instagram, Facebook, a bare WebView) is never given the camera (media/useCaptureCamera) and
  // seldom the location: once its answer is in, the way on is another browser — the location steps cannot help there.
  const webview = detectWebview(agent);
  // E4: a fix this phone gave that is held back — rough, or older than the visit by its own clock.
  const held = useLatestFix();
  const heldBack = held && !isCurrentFix(held) ? held : null;
  const rough = heldBack && isVisitFix(heldBack) && isRoughFix(heldBack) ? heldBack : null;
  const probing = watch.pending;

  // Code 1: refine the report with the browser's permission state (the iPhone is decided by platform first) — read
  // again on every answer of the watch, so a dismissal followed by a block reads as the block.
  const base = initialReason(phase, permission, platform);
  const [deniedDetail, setDeniedDetail] = useState<GateReason | null>(null);
  const isDenied = base === 'denied' || base === 'ios-reload';
  useEffect(() => {
    if (!isDenied) {
      setDeniedDetail(null);
      return undefined;
    }
    if (probing) return undefined;
    let cancelled = false;
    void queryPermissionState('geolocation').then((state) => {
      if (!cancelled) setDeniedDetail(reasonForDenied(state, platform));
    });
    return () => {
      cancelled = true;
    };
  }, [isDenied, probing, platform]);
  const reason: GateReason = isDenied ? deniedDetail ?? base : base;
  const read: GateReason = heldBack && (reason === 'waiting' || reason === 'off' || reason === 'timeout') ? 'refining' : reason;
  const shown: GateReason = webview && read !== 'waiting' && read !== 'refining' ? 'webview' : read;
  const shownRef = useRef(shown);
  shownRef.current = shown;

  const disposed = useRef(false);
  // The polite live region's words, and the reason they were last said for (follow-up C).
  const [spoken, setSpoken] = useState('');
  const spokenFor = useRef<GateReason | null>(null);
  const speakTimer = useRef<number | null>(null);
  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
      if (speakTimer.current !== null) window.clearTimeout(speakTimer.current);
    };
  }, []);

  // Restart the one watch; on a timer the accuracy alternates (a weak GPS indoors can still get a network position).
  const attempt = useRef(0);
  const { restart } = watch;
  /** `asked`: the customer pressed Try again (always said); otherwise said only for a reason not yet announced. */
  const tryNow = useCallback((asked = false): void => {
    if (asked || spokenFor.current !== shownRef.current) {
      spokenFor.current = shownRef.current;
      // emptied, then filled a moment later: the same words said again are a change a screen reader announces
      setSpoken('');
      if (speakTimer.current !== null) window.clearTimeout(speakTimer.current);
      speakTimer.current = window.setTimeout(() => {
        speakTimer.current = null;
        if (!disposed.current) setSpoken(t('sv.geo.gate.checking'));
      }, 100);
    }
    attempt.current += 1;
    restart({ highAccuracy: attempt.current % 2 === 1 });
  }, [restart, t]);
  const tryRef = useRef(tryNow);
  tryRef.current = tryNow;
  const pendingRef = useRef(probing);
  pendingRef.current = probing;

  // E4: hold a held-back fix for ROUGH_WAIT_MS while the watch brings a better one, then accept it. A watch that a
  // re-try left at low accuracy is put back to high while refining — a better fix needs the GPS.
  const refining = shown === 'refining';
  useEffect(() => {
    if (!refining) return undefined;
    if (!watch.highAccuracy) restart({ highAccuracy: true });
    const timer = window.setTimeout(acceptHeldFix, ROUGH_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [refining]); // eslint-disable-line react-hooks/exhaustive-deps -- once per refining spell

  // Self-healing: a timer only where the answer can change on its own; a return to the page and a permission change
  // always restart the watch (the customer may have just switched Location on, or allowed it, in the phone's settings).
  const waiting = shown === 'waiting';
  const timerOn = retriesOnTimer(shown);
  useEffect(() => {
    if (waiting || refining) return undefined;
    // While the page is hidden (the customer is in Settings) there is nothing to find: no restart, no GPS spent; a watch
    // that has not answered yet is not restarted under itself either.
    const fire = (): void => {
      if (document.visibilityState !== 'hidden' && !pendingRef.current) tryRef.current();
    };
    const timer = timerOn ? window.setInterval(fire, AUTO_RETRY_MS) : null;
    const onVisible = (): void => {
      // Back from Settings, Location perhaps just switched on: a watch left unanswered while the page was hidden must not
      // hold the fresh one — or Try again — back until its watchdog (up to 22 s). A restart supersedes it.
      if (document.visibilityState === 'visible') tryRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    let cancelled = false;
    let status: PermissionStatus | null = null;
    // A permission change is the customer's answer: restart at once, pending or not.
    const onChange = (): void => {
      if (document.visibilityState !== 'hidden') tryRef.current();
    };
    try {
      // Guarded: an engine may throw for the name instead of rejecting, or answer with no promise at all.
      const asked = navigator.permissions?.query?.({ name: 'geolocation' as PermissionName });
      if (asked) {
        void Promise.resolve(asked).then(
          (s) => {
            if (cancelled || !s) return;
            status = s;
            s.addEventListener?.('change', onChange);
          },
          () => undefined,
        );
      }
    } catch {
      /* no change events then — the timer, a return to the page and Try again still re-try */
    }
    return () => {
      cancelled = true;
      if (timer !== null) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      status?.removeEventListener?.('change', onChange);
    };
  }, [waiting, refining, timerOn]);

  const retryButton = (
    <Button onClick={() => tryNow(true)} disabled={probing} data-testid="location-gate-retry" variant={shown === 'ios-reload' ? 'soft' : 'primary'}>
      {t('sv.geo.gate.retry')}
    </Button>
  );

  if (waiting || refining) {
    return (
      <div className="app-view center" data-testid="location-gate" data-status="waiting" data-reason={shown}>
        <div className="center-col" style={{ maxWidth: '34ch' }}>
          <div className="app-preparing" role="status">
            {refining && rough ? t('sv.geo.gate.refining', { meters: String(Math.round(rough.accuracyM)) }) : t('sv.geo.gate.waiting')}
          </div>
          <div style={{ marginTop: 18 }}>{retryButton}</div>
        </div>
      </div>
    );
  }

  const steps =
    shown === 'off' ? locationOffStepsFor(platform)
    : shown === 'ios-reload' ? IOS_RELOAD_STEPS
    : [];
  const doReload = reload ?? ((): void => window.location.reload());
  return (
    <div className="app-view center" data-testid="location-gate" data-status={shown === 'denied' || shown === 'ios-reload' || shown === 'webview' ? 'denied' : 'off'} data-reason={shown}>
      <div className="center-col" style={{ maxWidth: '38ch' }}>
        <div className="h-title sm">{t(shown === 'webview' ? 'sv.cam.webview.title' : 'sv.geo.gate.title')}</div>
        <p className="sub" style={{ marginTop: 8 }}>{t('sv.geo.gate.why', { tenantName })}</p>
        {/* One alert element for the screen's life: its words change in place; a re-try never re-mounts it. */}
        <p className="sub" style={{ marginTop: 8 }} role="alert" data-testid="location-gate-lead">{t(LEAD[shown as keyof typeof LEAD])}</p>
        {shown === 'denied' ? <RecoveryCard kind="location" ua={agent} /> : null}
        {shown === 'webview' ? <p className="perm-autoresume mono" data-testid="location-gate-webview">{t('sv.perm.webview.tip')}</p> : null}
        {steps.length > 0 ? (
          <ol className="perm-steps" data-testid="location-off-steps">
            {steps.map((k) => (
              <li key={k}>{t(k, { app: iosLocationEntry(agent) ?? t('sv.perm.ios.app.other') })}</li>
            ))}
          </ol>
        ) : null}
        {/* seen: every re-try; heard: once per reason (the live region below) */}
        <p className="sub" aria-hidden="true" data-testid="location-gate-progress" style={{ marginTop: 8 }}>
          {probing ? t('sv.geo.gate.checking') : ''}
        </p>
        <p className="sv-sr-only" aria-live="polite" data-testid="location-gate-live">{spoken}</p>
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          {shown === 'ios-reload' ? (
            <Button onClick={doReload} data-testid="location-gate-reload">{t('sv.geo.gate.reload')}</Button>
          ) : null}
          {retryButton}
        </div>
      </div>
    </div>
  );
}
